import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { confirmVerifyEmail, confirmLoginEmailChange } from '../lib/api';

export default function VerifyEmail() {
  const [params] = useSearchParams();
  if (params.get('intent') === 'email-change') return <VerifyEmailChange key={params.get('token') || ''} token={params.get('token')} recovery={params.get('mode') === 'recovery'} />;
  return <VerifyFirstEmail />;
}

function VerifyFirstEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | ok | error

  useEffect(() => {
    const token = params.get('token');
    if (!token) { setStatus('error'); return; }
    let cancelled = false;
    (async () => {
      try {
        await confirmVerifyEmail(token);
        if (!cancelled) setStatus('ok');
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-sm text-center space-y-4" data-testid="verify-email-page">
        {status === 'loading' && (
          <>
            <Loader2 className="w-10 h-10 text-primary mx-auto animate-spin" />
            <p className="text-sm text-muted-foreground">Verifierar din e-post…</p>
          </>
        )}
        {status === 'ok' && (
          <>
            <CheckCircle2 className="w-12 h-12 text-[rgb(var(--tn-text-22c55e))] mx-auto" />
            <h1 className="text-lg font-semibold text-foreground">E-post verifierad</h1>
            <p className="text-sm text-muted-foreground">
              Din e-postadress är verifierad. Använd din inloggningsadress eller ditt befintliga användarnamn för att logga in.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              data-testid="verify-email-go-btn"
              className="text-sm font-medium px-4 py-2 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition"
            >
              Gå till Trainnova
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="w-12 h-12 text-destructive mx-auto" />
            <h1 className="text-lg font-semibold text-foreground">Länken kan inte användas</h1>
            <p className="text-sm text-muted-foreground">
              Länken är ogiltig, har gått ut eller redan använts. Be din tränare skicka en ny verifiering.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm font-medium px-4 py-2 rounded bg-secondary text-secondary-foreground hover:bg-secondary/80 transition"
            >
              Till inloggning
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function VerifyEmailChange({ token, recovery }) {
  const [status, setStatus] = useState(token ? 'ready' : 'error');
  const [message, setMessage] = useState('Länken saknas. Öppna länken i verifieringsmailet.');
  const busy = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const confirm = async () => {
    if (busy.current || !token) return;
    busy.current = true;
    setStatus('loading');
    try {
      const response = await confirmLoginEmailChange(token);
      if (!mounted.current) return;
      if (response.data?.success !== true || !['completed', 'awaiting_other_address'].includes(response.data?.status)) throw new Error('Unknown response');
      setStatus(response.data.status);
    } catch (error) {
      if (!mounted.current) return;
      const detail = error.response?.data?.detail;
      setMessage(typeof detail === 'string' ? detail : 'Resultatet kunde inte bekräftas. Försök igen med samma länk.');
      setStatus('error');
    } finally { busy.current = false; }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full rounded-2xl border border-border bg-card p-6 text-center space-y-4" data-testid="verify-email-change-page">
        <h1 className="text-xl font-semibold">Byt inloggningsadress</h1>
        {status === 'ready' && <p className="text-sm text-muted-foreground">{recovery ? 'En plattformsadministratör har begärt rättning efter identitetskontroll. Bekräfta bara om ärendet gäller ditt eget konto. När du bekräftar byts adressen och gamla sessioner avslutas.' : 'Bekräfta bara om du begärt adressbytet själv eller med hjälp av din ledare. Både den gamla och den nya adressen behöver godkännas. När båda är klara byts adressen och du behöver logga in igen.'}</p>}
        {status === 'loading' && <><Loader2 className="h-8 w-8 mx-auto animate-spin" /><p>Bekräftar…</p></>}
        {status === 'awaiting_other_address' && <><CheckCircle2 className="h-10 w-10 mx-auto text-[rgb(var(--tn-text-22c55e))]" /><p>Den här adressen är bekräftad.</p><p className="text-sm text-muted-foreground">Öppna även mailet till den andra adressen och bekräfta där. Den gamla adressen gäller tills båda är klara.</p></>}
        {status === 'completed' && <><CheckCircle2 className="h-10 w-10 mx-auto text-[rgb(var(--tn-text-22c55e))]" /><p>Adressbytet är klart.</p><p className="text-sm text-muted-foreground">Logga in med den nya adressen. Ditt lösenord, användarnamn, lag och barnkopplingar är oförändrade.</p></>}
        {status === 'error' && <p role="alert" className="text-sm text-destructive">{message}</p>}
        {token && ['ready', 'error', 'awaiting_other_address'].includes(status) && <button type="button" onClick={confirm} className="rounded-lg bg-primary px-4 py-2 text-primary-foreground">{status === 'ready' ? 'Bekräfta adressbyte' : status === 'awaiting_other_address' ? 'Kontrollera om bytet är klart' : 'Försök igen'}</button>}
        <button type="button" onClick={() => window.location.assign('/login?mode=password')} className="block mx-auto text-sm underline">Till inloggning</button>
      </div>
    </div>
  );
}
